import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import UploadDocumentModal from "../UploadDocumentModal";
import { CloudUpload, FileText, Trash2 } from "lucide-react";

const API_URL = "https://ims.holdthechild.net";

const FileItem = ({ file, onRemove }) => {
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemove = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsRemoving(true);
    await onRemove(file);
  };

  const getFileTypeColor = (fileName) => {
    if (!fileName || typeof fileName !== "string") {
      return "text-gray-600 bg-gray-50";
    }
    const ext = fileName.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return "text-red-600 bg-red-50";
      case "docx":
      case "doc":
        return "text-blue-600 bg-blue-50";
      case "xlsx":
      case "xls":
        return "text-green-600 bg-green-50";
      case "jpg":
      case "jpeg":
      case "png":
        return "text-purple-600 bg-purple-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  return (
    <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all duration-300">
      <div className="flex items-center p-4 space-x-4">
        <div className={`flex-shrink-0 p-3 rounded-lg transition-colors ${getFileTypeColor(file.originalName)}`}>
          <FileText className="w-6 h-6" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate pr-2">
            {file.originalName}
          </p>
          <div className="flex items-center space-x-3 mt-1">
            <span className="text-xs font-medium text-green-600">Ready</span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleRemove}
          disabled={isRemoving}
          className={`p-2 rounded-lg transition-all duration-200 ${
            isRemoving
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100"
          }`}
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

const AddInvoiceModal = ({ closeModal }) => {
  const [formData, setFormData] = useState({
    po: "",
    invoiceNumber: "",
    invoiceDate: "",
    dueDate: "",
    amount: "",
    description: "",
  });
  
  const [loading, setLoading] = useState(false);
  const [poList, setPoList] = useState([]);
  
  // Upload modal states
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const token = localStorage.getItem("token");
  const decoded = token ? JSON.parse(atob(token.split('.')[1])) : null;
  const staffId = decoded ? decoded.id : null;

  // Fetch all purchase orders
  useEffect(() => {
    const fetchPOs = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_URL}/api/purchase-orders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("Fetched POs from backend:", res.data);
        setPoList(res.data || []);
      } catch (err) {
        console.error("Failed to fetch POs:", err);
        toast.error("Failed to load Purchase Orders ❌");
      }
    };
    fetchPOs();
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleUploadSuccess = useCallback((files) => {
    setUploadedDocuments(files);
    setIsUploadModalOpen(false);
    toast.success(`${files.length} invoice document(s) uploaded successfully!`);
  }, []);

  const handleRemoveFile = useCallback(async (file) => {
    if (loading) return;
    setUploadedDocuments(prev => prev.filter(f => f !== file));
  }, [loading]);

  const handleUploadButtonClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUploadModalOpen(true);
  }, []);

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (uploadedDocuments.length === 0) {
    toast.error("Please upload at least one invoice document 📑");
    return;
  }
  if (!formData.po) {
    toast.error("Please select a Purchase Order 📋");
    return;
  }

  const selectedPO = poList.find((po) => po._id === formData.po);

  if (!selectedPO) {
    toast.error("Selected Purchase Order not found ❌");
    return;
  }

  setLoading(true);
  
  try {
    const token = localStorage.getItem("token");
    
    // ✅ NEW APPROACH: Get the actual file and upload it with the invoice
    const fileUrl = uploadedDocuments[0].fileUrl;
    
    // ✅ Download the file from S3 and convert to blob
    console.log("Downloading file from S3:", fileUrl);
    const fileResponse = await fetch(fileUrl);
    const fileBlob = await fileResponse.blob();
    
    // ✅ Create FormData with actual file
    const formDataToSend = new FormData();
    
    // Add form fields
    formDataToSend.append('po', selectedPO._id);
    formDataToSend.append('invoiceNumber', formData.invoiceNumber.trim());
    formDataToSend.append('invoiceDate', new Date(formData.invoiceDate).toISOString());
    formDataToSend.append('dueDate', formData.dueDate ? new Date(formData.dueDate).toISOString() : '');
    formDataToSend.append('amount', parseFloat(formData.amount).toString());
    formDataToSend.append('status', 'Pending Review');
    formDataToSend.append('description', formData.description?.trim() || `Invoice for ${selectedPO.poNumber}`);
    
    // ✅ Add the actual file (not just URL)
    formDataToSend.append('invoiceFile', fileBlob, uploadedDocuments[0].originalName);

    console.log("✅ Sending FormData with actual file:");
    console.log("File name:", uploadedDocuments[0].originalName);
    console.log("File size:", fileBlob.size);
    console.log("File type:", fileBlob.type);
    
    for (let pair of formDataToSend.entries()) {
      if (pair[1] instanceof File || pair[1] instanceof Blob) {
        console.log(pair[0] + ': [FILE] ' + pair[1].name || 'blob');
      } else {
        console.log(pair[0] + ': ' + pair[1]);
      }
    }

    const res = await axios.post(`${API_URL}/api/vendor-invoices`, formDataToSend, {
      headers: {
        Authorization: `Bearer ${token}`,
        // Don't set Content-Type for FormData - let axios handle it
      },
    });

    console.log("✅ SUCCESS - Invoice created with file upload:", res.data);
    toast.success(
      `Invoice added for ${selectedPO.poNumber} — ${selectedPO.vendorName} ✅`
    );
    closeModal();

  } catch (err) {
    console.error("=== DETAILED ERROR DEBUG ===");
    console.error("Full error:", err);
    console.error("Error response data:", err.response?.data);
    console.error("Error status:", err.response?.status);
    
    const message =
      err.response?.data?.error ||
      err.response?.data?.message ||
      err.message ||
      "Failed to add invoice ❌";
    toast.error(`Error: ${message}`);
  } finally {
    setLoading(false);
  }
};

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b">
            <h2 className="text-2xl font-bold text-gray-800">Add New Invoice</h2>
          </div>

          {/* Scrollable Content */}
          <div className="px-6 py-4 overflow-y-auto flex-1">
            <form
              id="add-invoice-form"
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              {/* Purchase Order Dropdown */}
              <div>
                <label className="block text-gray-700 mb-1">Purchase Order</label>
                <select
                  name="po"
                  value={formData.po}
                  onChange={handleChange}
                  required
                  className="w-full border p-2 rounded"
                >
                  <option value="">-- Select Purchase Order --</option>
                  {poList.map((po) => (
                    <option key={po._id} value={po._id}>
                      {po.poNumber} — {po.vendorName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Invoice Number */}
              <div>
                <label className="block text-gray-700 mb-1">Invoice Number</label>
                <input
                  type="text"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleChange}
                  required
                  className="w-full border p-2 rounded"
                />
              </div>

              
              <div>
                <label className="block text-gray-700 mb-1">Invoice Date</label>
                <input
                  type="date"
                  name="invoiceDate"
                  value={formData.invoiceDate}
                  onChange={handleChange}
                  required
                  className="w-full border p-2 rounded"
                />
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  className="w-full border p-2 rounded"
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-gray-700 mb-1">Amount</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  className="w-full border p-2 rounded"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full border p-2 rounded"
                />
              </div>

              {/* Invoice Documents Upload */}
              <div>
                <label className="block text-gray-700 mb-2">
                  Invoice Documents
                  {uploadedDocuments.length > 0 && (
                    <span className="ml-2 inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                      {uploadedDocuments.length} file(s) ready
                    </span>
                  )}
                </label>
                
                <button
                  type="button"
                  onClick={handleUploadButtonClick}
                  disabled={loading}
                  className="w-full border-2 border-dashed border-gray-300 rounded-md p-6 text-center text-gray-500 hover:border-orange-500 hover:text-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CloudUpload className="mx-auto h-8 w-8 mb-2" />
                  Click to Upload Invoice Documents
                </button>

                {uploadedDocuments.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <h4 className="text-sm font-medium text-gray-800">
                      Uploaded Files ({uploadedDocuments.length})
                    </h4>
                    <div className="space-y-2">
                      {uploadedDocuments.map((file, index) => (
                        <FileItem
                          key={`${file.originalName}-${index}`}
                          file={file}
                          onRemove={handleRemoveFile}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t flex justify-end gap-3">
            <button
              type="button"
              onClick={closeModal}
              disabled={loading}
              className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="add-invoice-form"
              disabled={loading}
              className={`px-4 py-2 rounded text-white ${
                loading
                  ? "bg-orange-400 cursor-not-allowed"
                  : "bg-orange-600 hover:bg-orange-700"
              }`}
            >
              {loading ? "Saving..." : "Save Invoice"}
            </button>
          </div>
        </div>
      </div>

      {/* Upload Document Modal */}
      <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        staffId={staffId}
        onUploadSuccessWithFiles={handleUploadSuccess}
        category="Invoice Documents"
      />
    </>
  );
};

export default AddInvoiceModal;
